/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Tenant "Contact" page: hero + the tenant's office locations that have public contact details
 * (an email or a phone), with their address, tel: and mailto: links. Data comes from the owner's
 * Locations (Company → Locations), surfaced via /storefront/contact-locations.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail } from 'lucide-react';
import type { StorefrontContactLocation } from '../types';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import PageHero from '../components/PageHero';
import { pickLocalized } from '../utils/localized';

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const { tenant } = useTenant();
  const name = tenant?.displayName || t('contact.us', 'us');

  const [offices, setOffices] = useState<StorefrontContactLocation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .getContactLocations()
      .then(setOffices)
      .catch(() => setOffices([]))
      .finally(() => setLoading(false));
  }, []);

  const addressOf = (o: StorefrontContactLocation) =>
    [o.addressLine1, o.addressLine2, [o.city, o.state, o.zipCode].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join('\n');

  // The Design-editor contact block (one per storefront) — shown first, before office locations.
  const primary = {
    name: tenant?.displayName || name,
    address: pickLocalized(tenant?.contactAddressJson, tenant?.contactAddress, i18n.language).trim(),
    phone: (tenant?.contactPhone || '').trim(),
    email: (tenant?.contactEmail || '').trim(),
  };
  const hasPrimary = !!(primary.address || primary.phone || primary.email);

  const ContactCard = ({ title, address, phone, email }: { title: string; address?: string; phone?: string; email?: string }) => (
    <div className="bg-white rounded-lg shadow-lg p-6 text-gray-700">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-3">
        {address && (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
            <span className="whitespace-pre-line">{address}</span>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
            <a href={`tel:${phone.replace(/\s+/g, '')}`} className="hover:underline">{phone}</a>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
            <a href={`mailto:${email}`} className="hover:underline break-all">{email}</a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <PageHero title={t('contact.title', 'Contact {{name}}', { name })} />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-primary)' }} />
          </div>
        ) : !hasPrimary && offices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 text-gray-700 text-center text-lg">
            {t('contact.none', "We'd love to hear from you. Please check back soon for our contact details.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {hasPrimary && (
              <ContactCard title={primary.name} address={primary.address} phone={primary.phone} email={primary.email} />
            )}
            {offices.map((o) => (
              <ContactCard key={o.id} title={o.name} address={addressOf(o)} phone={o.phone || undefined} email={o.email || undefined} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
